import { useFormContext, useWatch } from "react-hook-form";
import AlarmSetpointEditorUtils, { SetPointAdminMode, SetpointFormType} from "../AlarmSetpointEditorUtils";
import { Alert, Box,  Stack  } from "@mui/material";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import { isMobile, isTablet } from "react-device-detect";
import SideSheet from "../../side-sheet/side-sheet/SideSheet";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../forms/form-property/FormProperty";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useGroup } from "../../../queries/groups/UseGroup";
import { MessagingTypeEnum, UseAlarmSetpointMessagingStateType } from "./AlarmSetpointMessagingState";
import SideSheetRadioGroup from "../../side-sheet/side-sheet-radio-group/SideSheetRadioGroup";
import { useTranslation } from "react-i18next";
import FormColorPicker from "../../forms/form-color-picker/FormColorPicker";
import FormInput from "../../forms/form-input/FormInput";
import { useEffect, useMemo } from "react";
import AlarmSetpointMessagingUtils, { MessageTemplateTypeEnum } from "./AlarmSetpointMessagingUtils";
import { useSetpointReferenceData } from "../../../queries/alarm-setpoint-admin/UseSetpointReferenceData";
import { IReferenceDTO, SetPointPrecedenceTypeEnum } from "../../../services/setpoint-administration-service/Models";
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import AlarmSetpointMessageEditor from "./AlarmSetpointMessageEditor";
import _ from "lodash";
import { useCustomerAttribute } from "../../../queries/attributes/UseCustomerAttribute";
import { useObjectProperties } from "../../../queries/object-properties/UseObjectProperties";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { UseQueryResult } from "react-query";
import { ITagProperties } from "../../../services/properties-data-service/Models";

export type AlarmSetpointMessagingProps = {
    groupID: number,
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;
    setViewOptionState?: (value: ViewOptionStateEnum) => void;
    setpointMessagingState: UseAlarmSetpointMessagingStateType
};

export default function AlarmSetpointMessaging({
    groupID,
    openSideSheet,
    onCloseSideSheet,
    //setViewOptionState,
    setpointMessagingState
    
}: AlarmSetpointMessagingProps) {

    const { t } = useTranslation();
    const { confirm
        //, acknowledge 
    } = useConfirmation();


    const [userState] = useUserContext();

    const {
        state, 
        setMessagingType,
        resetState

    } = setpointMessagingState;

    const {
        getValues,
        watch,
        trigger,
        control,
        reset,
        setValue
    } = useFormContext<SetpointFormType>();    


    const setpoint = useWatch({ control: control }) as SetpointFormType;    

    const mode = (setpoint?.setPointID ?? 0) <= 0 ? SetPointAdminMode.Add : SetPointAdminMode.Edit;
    
    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });

    const {data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID ,
        name: 'allow voice alarm',
        enabled: group !== undefined
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute?.attributeValue.toLowerCase() === 'true';

    const organizationID = setpoint.setPointID > 0 ? setpoint.organizationID : group?.organizationID as number;

    const {data: referenceData /*status: referenceStatus */} = useQueryNotifications(useSetpointReferenceData)({
        groupID ,
        organizationID: organizationID,
        enabled: groupID !== undefined
    });

    // const tagID = useMemo(() => {
    //     const firstSelectedTag = setpoint?.devices?.flatMap(d => d.tags ?? []).find(tag => tag.selected == true);
    //     return firstSelectedTag?.id;
    // }, [setpoint?.devices]);
    
    const tagID = useMemo(() => {
        const firstSelectedTag = setpoint?.deviceTags?.[0];
        return firstSelectedTag?.tagID;
    }, [setpoint?.deviceTags]);

    const { data: tagProperties } = useQueryNotifications(useObjectProperties)({        
        groupID: groupID,
        eLynxObject: {
            id: tagID ?? 0,
            objectType: eLynxObjectTypeEnum.Tag
        },
        enabled: //mode === SetPointAdminMode.Edit && 
        tagID ? true : false
    }) as UseQueryResult<ITagProperties | undefined>;

    const customMessaging = watch('customMessaging');

    const isPhone = isMobile && !isTablet;

    async function onMessagingTypeChanged(value: MessagingTypeEnum) {

        if (value === MessagingTypeEnum.Default) {

            const result = await confirm({
                dialogTitle: t("setpointAdmin.messaging.warning.title"),
                confirmationText: t("setpointAdmin.messaging.warning.message"),
                yesButtonText: t("buttons.yes"),
                noButtonText: t("buttons.no"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: true,
                showCloseButton: true
            });

            if (result  !== "yes") {
                return;
            }


            const messageTemplates = AlarmSetpointEditorUtils.getMessageTemplates(setpoint, referenceData as IReferenceDTO);

            const copy = _.cloneDeep(setpoint);
            copy.emailAlarmMessageSubject = messageTemplates.emailAlarmMessageSubject;
            copy.emailAlarmMessageBody = messageTemplates.emailAlarmMessageBody;
            copy.emailClearMessageSubject = messageTemplates.emailClearMessageSubject;
            copy.emailClearMessageBody = messageTemplates.emailClearMessageBody;
            copy.voiceAlarmMessageBody = allowVoiceAlarm ? messageTemplates.emailAlarmMessageBody : messageTemplates.voiceAlarmMessageBody;
            copy.voiceClearMessageBody = allowVoiceAlarm ? messageTemplates.emailClearMessageBody : messageTemplates.voiceClearMessageBody;

            // clear the sample messages so they can be reconstructed.
            copy.sampleEmailAlarmMessageSubject = '';
            copy.sampleEmailAlarmMessageBody = '';
            copy.sampleEmailClearMessageSubject = '';
            copy.sampleEmailClearMessageBody = '';
            copy.sampleVoiceAlarmMessageBody = '';
            copy.sampleVoiceClearMessageBody = '';

            // if (value === MessagingTypeEnum.Default) {
            //     copy.customMessaging = false;
            // } else {
            //     copy.customMessaging = true;
            // }

            reset(copy, {
                keepDirty: true
            });

            trigger();
        }

        setMessagingType(value);

        setValue('customMessaging', value === MessagingTypeEnum.Custom, {shouldDirty: true});
    }

    useEffect(() => {

        
        
        if (setpoint && tagProperties) {

            const subject = getValues('emailAlarmMessageSubject');
            if (subject && subject.length > 0 && (setpoint.sampleEmailAlarmMessageSubject?.trim().length ?? 0) === 0) {
                const text = AlarmSetpointMessagingUtils.formatSampleMessage(setpoint, subject, referenceData?.unitsOfMeasure ?? [], tagProperties); 

                if (getValues('sampleEmailAlarmMessageSubject')?.localeCompare(text.trim()) !== 0) {
                    setValue('sampleEmailAlarmMessageSubject', text);
                    trigger('sampleEmailAlarmMessageSubject');
                }
            }

            const body = getValues('emailAlarmMessageBody');
            if (body && body.length > 0 && (setpoint.sampleEmailAlarmMessageBody?.trim().length ?? 0) === 0) {
                const text = AlarmSetpointMessagingUtils.formatSampleMessage(setpoint, body, referenceData?.unitsOfMeasure ?? [], tagProperties); 

                if (getValues('sampleEmailAlarmMessageBody')?.localeCompare(text.trim()) !== 0) {
                    setValue('sampleEmailAlarmMessageBody', text);
                    trigger('sampleEmailAlarmMessageBody');
                }
            }

            const clearSubject = getValues('emailClearMessageSubject');
            if (clearSubject && clearSubject.length > 0 && (setpoint.sampleEmailClearMessageSubject?.trim().length ?? 0) === 0) {
                const text = AlarmSetpointMessagingUtils.formatSampleMessage(setpoint, clearSubject, referenceData?.unitsOfMeasure ?? [], tagProperties); 

                if (getValues('sampleEmailClearMessageSubject')?.localeCompare(text.trim()) !== 0) {
                    setValue('sampleEmailClearMessageSubject', text);
                    trigger('sampleEmailClearMessageSubject');
                }
            }

            const clearBody = getValues('emailClearMessageBody');
            if (clearBody && clearBody.length > 0 && (setpoint.sampleEmailClearMessageBody?.trim().length ?? 0) === 0) {
                const text = AlarmSetpointMessagingUtils.formatSampleMessage(setpoint, clearBody, referenceData?.unitsOfMeasure ?? [], tagProperties); 

                if (getValues('sampleEmailClearMessageBody')?.localeCompare(text.trim()) !== 0) {
                    setValue('sampleEmailClearMessageBody', text);
                    trigger('sampleEmailClearMessageBody');
                }
            }


            const voiceBody = getValues('voiceAlarmMessageBody');
            if (voiceBody && voiceBody.length > 0 && (setpoint.sampleVoiceAlarmMessageBody?.trim().length ?? 0) === 0) {
                const text = AlarmSetpointMessagingUtils.formatSampleMessage(setpoint, voiceBody, referenceData?.unitsOfMeasure ?? [], tagProperties); 

                if (getValues('sampleVoiceAlarmMessageBody')?.localeCompare(text.trim()) !== 0) {
                    setValue('sampleVoiceAlarmMessageBody', text);
                    trigger('sampleVoiceAlarmMessageBody');
                }
            }
            const voiceClearBody = getValues('voiceClearMessageBody');
            if (voiceClearBody && voiceClearBody.length > 0 && (setpoint.sampleVoiceClearMessageBody?.trim().length ?? 0) === 0) {
                const text = AlarmSetpointMessagingUtils.formatSampleMessage(setpoint, voiceClearBody, referenceData?.unitsOfMeasure ?? [], tagProperties); 

                if (getValues('sampleVoiceClearMessageBody')?.localeCompare(text.trim()) !== 0) {
                    setValue('sampleVoiceClearMessageBody', text);
                    trigger('sampleVoiceClearMessageBody');
                }
            }
        }
    }, [
        setpoint,
        referenceData,
        tagProperties,
        getValues,
        setValue,
        trigger
    ]);    

    useEffect(() => {
       
        setMessagingType(customMessaging ? MessagingTypeEnum.Custom : MessagingTypeEnum.Default);
        setValue('customMessaging', customMessaging, {shouldDirty: true});
    }, [
        customMessaging,
        setMessagingType,
        setValue
    ]);

   return (
       <Box
           sx={{
               display: 'flex',
               flexDirection: 'column',
               flex: '1 1 auto',
               overflow: 'hidden',
               position: 'relative',
               overflowY: 'auto'
           }}>
           <Stack
               direction='row'
               sx={{
                   display: 'flex',
                   flex: '1 1 auto'
               }}> 
               <Box
                   sx={{
                   display: 'flex',
                   flexDirection: "column",
                   flex: '1 1 auto',
                   overflow: "hidden",
                   padding: 1
                   }}>


                   <FormPropertySection label={t('setpointAdmin.colors.title')}>

                       <FormProperty>
                           <FormColorPicker 
                               id="alarmColor"
                                   name="alarmColor"
                                   label={t('setpointAdmin.colors.alarm')}
                                   sx={{ 
                                       width: 1,
                                       marginTop: 1,
                                       display: 'flex'
                                   }}
                                   //onChange={() => onSetpointTypeChange()}
                               />

                       </FormProperty>

                       <FormProperty>
                           <FormColorPicker 
                               id="acknowledgeColor"
                                   name="acknowledgeColor"
                                   label={t('setpointAdmin.colors.acknowledge')}
                                   sx={{ 
                                       width: 1,
                                       marginTop: 1,
                                       display: 'flex'
                                   }}
                                   //onChange={() => onSetpointTypeChange()}
                               />
                   
                       </FormProperty>

                   </FormPropertySection>

                   {(setpoint.isDiscrete || setpoint.setPointType === SetPointPrecedenceTypeEnum.Field) && 
                   <FormPropertySection label={t('setpointAdmin.fieldAlarmOptions')}>

                       <FormProperty sx={{paddingBottom: 0}}>
                           <FormCheckbox
                               name='suppressAlarmAlarm'
                               label={t('setpointAdmin.messaging.suppressAlarmAlarmLabel')}
                               sx={{ 
                                   marginRight: 0,
                                   marginTop: 0,
                                   //textOverflow: "ellipsis",
                                   //whiteSpace: "nowrap",
                                   //overflow: "hidden",
                                   '& > *': {
                                       fontSize: '.9rem'
                                   }
                               }} />

                       </FormProperty>

                       <FormProperty sx={{paddingBottom: 0}}>
                           <FormCheckbox
                               name='suppressNormalNormal'
                               label={t('setpointAdmin.messaging.suppressNormalNormalLabel')}
                               sx={{ 
                                   marginRight: 0,
                                   marginTop: 0,
                                   //textOverflow: "ellipsis",
                                   //whiteSpace: "nowrap",
                                   //overflow: "hidden",
                                   '& > *': {
                                       fontSize: '.9rem'
                                   }
                               }} />

                       </FormProperty>



                   </FormPropertySection>
                   }

                   <FormPropertySection label={t('setpointAdmin.messaging.exampleAlarm')}>
                   <FormProperty>
                       <FormInput
                           name="sampleEmailAlarmMessageSubject"
                           label={t('setpointAdmin.messaging.subject')}
                           sx={{
                               width: 1,
                               marginTop: 1,
                           }}
                           readOnly={true}
                           multiline={true}
                           rows={4}
                       /> 
                   </FormProperty>

                   <FormProperty>
                       <FormInput
                           name="sampleEmailAlarmMessageBody"
                           label={t('setpointAdmin.messaging.body')}
                           sx={{
                               width: 1,
                               marginTop: 1,
                           }}
                           readOnly={true}
                           multiline={true}
                           rows={4}
                       /> 
                   </FormProperty>


                   {allowVoiceAlarm && 
                   <FormProperty>
                       <FormInput
                           name="sampleVoiceAlarmMessageBody"
                           label={t('setpointAdmin.messaging.voice')}
                           sx={{
                               width: 1,
                               marginTop: 1,
                           }}
                           readOnly={true}
                           multiline={true}
                           rows={4}
                       /> 
                   </FormProperty>
                   }

                   </FormPropertySection>


                   {state.messagingType === MessagingTypeEnum.Custom &&

                       <>

                           <Alert severity="info"
                                   sx={{
                                       marginTop: 1,
                                       //display: 'flex',
                                       //flex: '1 1 auto',
                                       marginRight: 2
                                   }}>
                                   {t('setpointAdmin.messaging.reusableHelp')}
                           </Alert>

                           <FormPropertySection label=''>

                               <AlarmSetpointMessageEditor 
                                   groupID={groupID} 
                                   templateType={MessageTemplateTypeEnum.AlarmSubject} />
                           
                               <AlarmSetpointMessageEditor 
                                   groupID={groupID} 
                                   templateType={MessageTemplateTypeEnum.AlarmMessage} />
                           
                               {allowVoiceAlarm && <AlarmSetpointMessageEditor 
                                   groupID={groupID} 
                                   templateType={MessageTemplateTypeEnum.VoiceAlarmMessage} />
                               }
                               
                           </FormPropertySection>
                       </>

                   }



                   <FormPropertySection label={t('setpointAdmin.messaging.exampleClear')}>



                   <FormProperty>
                       <FormInput
                           name="sampleEmailClearMessageSubject"
                           label={t('setpointAdmin.messaging.subject')}
                           sx={{
                               width: 1,
                               marginTop: 1,
                           }}
                           readOnly={true}
                           multiline={true}
                           rows={4}
                       /> 
                   </FormProperty>


                   <FormProperty>
                       <FormInput
                           name="sampleEmailClearMessageBody"
                           label={t('setpointAdmin.messaging.body')}
                           sx={{
                               width: 1,
                               marginTop: 1,
                           }}
                           readOnly={true}
                           multiline={true}
                           rows={4}
                       /> 
                   </FormProperty>


                   {allowVoiceAlarm && 
                   <FormProperty>
                       <FormInput
                           name="sampleVoiceClearMessageBody"
                           label={t('setpointAdmin.messaging.voice')}
                           sx={{
                               width: 1,
                               marginTop: 1,
                           }}
                           readOnly={true}
                           multiline={true}
                           rows={4}
                       /> 
                   </FormProperty>
                   }




                   </FormPropertySection>

                   {state.messagingType === MessagingTypeEnum.Custom &&

                       <>
                       <Alert severity="info"
                       sx={{
                           //display: 'flex',
                           marginTop: 1,
                           //flex: '1 1 auto',
                           marginRight: 2
                       }}>
                       {t('setpointAdmin.messaging.reusableHelp')}
                       </Alert>

                       <FormPropertySection label=''>


                           <AlarmSetpointMessageEditor 
                               groupID={groupID} 
                               templateType={MessageTemplateTypeEnum.ClearSubject} />

                           <AlarmSetpointMessageEditor 
                               groupID={groupID} 
                               templateType={MessageTemplateTypeEnum.ClearMessage} />

                           {allowVoiceAlarm && <AlarmSetpointMessageEditor 
                               groupID={groupID} 
                               templateType={MessageTemplateTypeEnum.VoiceClearMessage} />
                           }

                       </FormPropertySection>
                       </>

                   }



               </Box>

               <SideSheet
                   title={t("options")}
                   open={openSideSheet ?? false}
                   hideCloseButton={!isPhone}
                   onClose={() => onCloseSideSheet && onCloseSideSheet()}
                   onReset={resetState}
                   >

                   <SideSheetRadioGroup
                       label={t('setpointAdmin.messaging.title')}
                       value={state.messagingType}
                       radioButtons={[{
                           label: t('setpointAdmin.messaging.default'),
                           ariaLabel: "default messaging",
                           value: MessagingTypeEnum.Default
                       },{
                           label: t('setpointAdmin.messaging.custom'),
                           ariaLabel: "custom messaging",
                           value: MessagingTypeEnum.Custom
                       }]}
                       onChange={(event) => onMessagingTypeChanged(+event.target.value)}
                   />

               </SideSheet>

           </Stack>
       </Box>
   );
  }
